import React, { useState } from "react";
import { api } from "../../../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../../../components/ui/dialog";
import { toast } from "sonner";

export function CashAbuseChip({ check, klass, dotClass }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/cash-request-abuse");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load abuse details");
    } finally {
      setBusy(false);
    }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) load();
  };

  const banIp = async (ip) => {
    try {
      await api.post("/admin/cash-request-abuse/ban-ip", { ip });
      toast.success(`Banned ${ip}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ban failed");
    }
  };

  const unbanIp = async (ip) => {
    try {
      await api.post("/admin/cash-request-abuse/unban-ip", { ip });
      toast.success(`Unbanned ${ip}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unban failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          title={`${check.note} · ${check.value} · click for drilldown`}
          className={`${klass} hover:ring-2 hover:ring-offset-1 hover:ring-current/30 cursor-pointer`}
          data-testid="deployment-chip-abuse"
        >
          <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} aria-hidden="true" />
          {check.label}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-[#FBF9F6] border-[#E5E0D8]" data-testid="cash-abuse-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-3xl">Cash-request abuse · last 24h</DialogTitle>
          <DialogDescription className="text-[#4A5C50]">
            Every 429 / banned hit is logged. Ban an IP to block future requests
            from that network — useful evidence for Twilio's abuse team if you
            need to escalate.
          </DialogDescription>
        </DialogHeader>
        {busy && <p className="text-sm text-[#4A5C50]">Loading…</p>}
        {data && (
          <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
            <p className="text-sm text-[#112318]">
              Total hits: <span className="mono font-semibold">{data.total_24h}</span>
            </p>

            <section>
              <h4 className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mb-2">Top offending IPs</h4>
              {data.top_ips.length === 0 && <p className="text-xs text-[#4A5C50]">No IP hits in the last 24h.</p>}
              <ul className="divide-y divide-[#E5E0D8] bg-white border border-[#E5E0D8] rounded-xl">
                {data.top_ips.map((row) => {
                  const isBanned = data.banned_ips.some((b) => b.ip === row.ip);
                  return (
                    <li key={row.ip} className="flex items-center gap-3 p-3" data-testid={`abuse-ip-${row.ip}`}>
                      <span className="mono text-sm flex-1 truncate">{row.ip}</span>
                      <span className="text-xs text-[#4A5C50] mono">{row.count} hits</span>
                      {isBanned ? (
                        <button type="button" onClick={() => unbanIp(row.ip)}
                          className="text-xs px-2.5 py-1 rounded-lg border border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/10"
                          data-testid={`abuse-unban-${row.ip}`}>Unban</button>
                      ) : (
                        <button type="button" onClick={() => banIp(row.ip)}
                          className="text-xs px-2.5 py-1 rounded-lg border border-[#D96C4A]/40 text-[#B04E2F] hover:bg-[#D96C4A]/10"
                          data-testid={`abuse-ban-${row.ip}`}>Ban</button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>

            <section>
              <h4 className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mb-2">Top phones</h4>
              {data.top_phones.length === 0 && <p className="text-xs text-[#4A5C50]">No phone hits in the last 24h.</p>}
              <ul className="divide-y divide-[#E5E0D8] bg-white border border-[#E5E0D8] rounded-xl">
                {data.top_phones.map((row) => (
                  <li key={row.phone} className="flex items-center gap-3 p-3">
                    <span className="mono text-sm flex-1 truncate">{row.phone}</span>
                    <span className="text-xs text-[#4A5C50] mono">{row.count} hits</span>
                  </li>
                ))}
              </ul>
            </section>

            {data.banned_ips.length > 0 && (
              <section>
                <h4 className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mb-2">Currently banned IPs</h4>
                <ul className="divide-y divide-[#E5E0D8] bg-white border border-[#E5E0D8] rounded-xl">
                  {data.banned_ips.map((b) => (
                    <li key={b.ip} className="flex items-center gap-3 p-3">
                      <span className="mono text-sm flex-1 truncate">{b.ip}</span>
                      <span className="text-[10px] text-[#4A5C50]">by {b.banned_by}</span>
                      <button type="button" onClick={() => unbanIp(b.ip)}
                        className="text-xs px-2.5 py-1 rounded-lg border border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/10"
                        data-testid={`abuse-unban-banned-${b.ip}`}>Unban</button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section>
              <h4 className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mb-2">Recent hits</h4>
              {data.recent.length === 0 && <p className="text-xs text-[#4A5C50]">No hits.</p>}
              <ul className="divide-y divide-[#E5E0D8] bg-white border border-[#E5E0D8] rounded-xl text-xs">
                {data.recent.map((h) => (
                  <li key={h.id} className="flex items-center gap-3 p-2.5">
                    <span className="mono text-[10px] text-[#4A5C50] w-36 shrink-0">{h.ts}</span>
                    <span className="text-[10px] uppercase tracking-widest text-[#B04E2F] w-32 shrink-0">{h.kind?.replace("cash_request_", "")}</span>
                    <span className="mono truncate">{h.phone}</span>
                    <span className="mono text-[#4A5C50] truncate">{h.ip}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
